import { CompassusNavigatorServiceImplFactory } from "../../core/impl/compassusNavigatorServiceImpl";

export const CompassusNavigatorControllerFactory = ({
  compassusNavigatorService,
}: {
  compassusNavigatorService: ReturnType<
    typeof CompassusNavigatorServiceImplFactory
  >;
}) => {
  const getPatients = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: ICompassusNavigatorQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatients({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Fetches just the patient ids matching the current filters (ids_only=true
   * on the navigator endpoint). Cheap enough that callers can skip page_size
   * entirely and get every match. See CompassusNavigator hook's
   * fetchAllMatchingPatientIds for usage.
   */
  const getPatientIds = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Omit<ICompassusNavigatorQueryParams, "ids_only">;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientIds({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const exportNavigator = async ({
    clinicId,
    body,
  }: {
    clinicId: number;
    body: ICompassusNavigatorExportRequest;
  }) => {
    try {
      const result = await compassusNavigatorService.exportNavigator({
        clinicId,
        body,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Pick<
      ICompassusNavigatorQueryParams,
      "search" | "admission_status" | "acuity_levels"
    >;
  }) => {
    try {
      const result = await compassusNavigatorService.getCounts({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getPayerFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getMedicationFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getMedicationFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getAllergyFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getAllergyFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getConditionFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getConditionFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getLastVisitProviderFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getLastVisitProviderFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getNextVisitProviderFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getNextVisitProviderFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getTodoTagFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getTodoTagFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCustomTagFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getCustomTagFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getClinicalRiskScoreFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getClinicalRiskScoreFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }: {
    clinicId: number;
    patientId: number;
    todoTags: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchPatientTodo({
        clinicId,
        patientId,
        todoTags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientCustomTags = async ({
    clinicId,
    patientId,
    tags,
    hospitalizationRecords,
  }: {
    clinicId: number;
    patientId: number;
    tags: string[];
    hospitalizationRecords?: ICompassusHospitalizationRecord[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchPatientCustomTags({
        clinicId,
        patientId,
        tags,
        hospitalizationRecords,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientCustomTags = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientCustomTags({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientAssociations = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientAssociations({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Bulk to-do mutation. Swap in for N parallel `patchPatientTodo` calls.
   * See IBulkPatchCompassusPatientTodo for mode semantics.
   */
  const bulkPatchPatientTodo = async ({
    clinicId,
    patientIds,
    mode,
    todoTags,
  }: {
    clinicId: number;
    patientIds: number[];
    mode: CompassusBulkTodoMode;
    todoTags?: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.bulkPatchPatientTodo({
        clinicId,
        patientIds,
        mode,
        todoTags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const putWatchlist = async ({
    clinicId,
    items,
  }: {
    clinicId: number;
    items: ICompassusWatchlistItem[];
  }) => {
    try {
      const result = await compassusNavigatorService.putWatchlist({
        clinicId,
        items,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : undefined,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getAcuityLevelFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getAcuityLevelFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Per-patient trend endpoints. Items are newest-first, one entry per
   * source visit, no roll-up. Safe to plot directly. Prefer reading
   * `latest_*` fields off `ICompassusPatient` when the list payload is
   * available; reach for these only when drawing a trend or detail view
   * that needs the full history.
   */
  const getPatientAcuityLevelHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getPatientAcuityLevelHistory({
          clinicId,
          patientId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientPrognosisHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientPrognosisHistory(
        {
          clinicId,
          patientId,
        }
      );
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientFormHistory = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatientFormHistory({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  /**
   * Structural schema for every Compassus assessment form. Pure projection
   * of the pipeline templates — body is identical per clinic and only
   * changes on deploy, so the FE should cache with a singleton SWR key
   * and `useSWRImmutable` (see `FormTrendCard`).
   */
  const getFormsSchema = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getFormsSchema({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  return {
    getPatients,
    getPatientIds,
    exportNavigator,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getCustomTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    patchPatientCustomTags,
    getPatientCustomTags,
    getPatientAssociations,
    bulkPatchPatientTodo,
    putWatchlist,
    getAcuityLevelFilters,
    getPatientAcuityLevelHistory,
    getPatientPrognosisHistory,
    getPatientFormHistory,
    getFormsSchema,
  };
};
